import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea, CartesianGrid } from "recharts";
import { ArrowLeft, Download, Activity, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function DeviceHistory() {
  const { device_id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(720); // 30 days

  useEffect(() => {
    api.get(`/devices/${device_id}/history`, { params: { hours } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load history"));
  }, [device_id, hours]);

  if (!data) return <div className="text-gray-500">Loading…</div>;
  const { device, parameter, unit, thresholds, readings } = data;

  const chartData = readings.map((r) => ({
    ts: new Date(r.recorded_at).toLocaleDateString([], { month: "short", day: "numeric" }) + " " + new Date(r.recorded_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    value: r.value,
    status: r.status,
  }));

  const exportCsv = () => {
    const lines = ["recorded_at,parameter,value,unit,status"];
    readings.forEach((r) => lines.push(`${r.recorded_at},${r.parameter},${r.value},${r.unit},${r.status}`));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${device.device_id}-history.csv`; a.click();
  };

  const lastReading = readings[readings.length - 1];
  const statusTone = device.status === "online" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300";

  return (
    <div className="space-y-6" data-testid="device-history-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => nav("/devices")} className="mb-2 -ml-2"><ArrowLeft className="w-4 h-4 mr-1" strokeWidth={2.5} />Back to devices</Button>
          <div className="text-xs text-gray-500">Devices → {device.device_name}</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">{device.device_name}</h1>
          <p className="text-sm text-gray-500 mt-1 font-mono">{device.device_id}</p>
        </div>
        <div className="flex items-center gap-2">
          {[24, 168, 720].map((h) => (
            <Button key={h} variant={hours === h ? "default" : "ghost"} onClick={() => setHours(h)} className={hours === h ? "f360-gradient border-0" : "bg-white/5 border border-white/10"} size="sm">
              {h === 24 ? "24h" : h === 168 ? "7d" : "30d"}
            </Button>
          ))}
          <Button onClick={exportCsv} variant="outline" className="border-gray-200" data-testid="export-history-btn"><Download className="w-4 h-4 mr-1" strokeWidth={2.5} />CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass p-4 rounded-lg"><div className="text-[10px] uppercase text-gray-500">Type</div><div className="text-lg font-bold mt-1">{device.device_type}</div></div>
        <div className="glass p-4 rounded-lg"><div className="text-[10px] uppercase text-gray-500">Site</div><div className="text-sm font-bold mt-1 truncate">{device.site_id}</div></div>
        <div className="glass p-4 rounded-lg"><div className="text-[10px] uppercase text-gray-500">Status</div><Badge className={`${statusTone} border-0 mt-1`}>{device.status}</Badge></div>
        <div className="glass p-4 rounded-lg"><div className="text-[10px] uppercase text-gray-500">Last Reading</div><div className="text-lg font-bold mt-1 f360-gradient-text">{lastReading ? `${lastReading.value} ${unit}` : "—"}</div></div>
      </div>

      <div className="glass p-5 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Telemetry — last {hours === 24 ? "24 hours" : hours === 168 ? "7 days" : "30 days"}</h3>
          <span className="text-xs text-gray-500">Parameter: <span className="text-orange-400 font-bold">{parameter}</span> ({unit})</span>
        </div>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="ts" stroke="#64748b" fontSize={10} tick={{ fontSize: 10 }} />
              <YAxis stroke="#64748b" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
              {thresholds?.critical && <ReferenceArea y1={0} y2={thresholds.critical} fill="#ef4444" fillOpacity={0.08} />}
              {thresholds?.warn && thresholds?.critical && <ReferenceArea y1={thresholds.critical} y2={thresholds.warn} fill="#f59e0b" fillOpacity={0.08} />}
              <Line type="monotone" dataKey="value" stroke="#ff6b35" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : <div className="text-gray-500 text-center py-12">No readings in this period.</div>}
      </div>

      <div className="glass rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2"><Activity className="w-4 h-4 text-orange-400" strokeWidth={2.5} /><h3 className="font-semibold">Recent Readings</h3></div>
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Value</TableHead><TableHead>Unit</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {readings.slice(-100).reverse().map((r, i) => {
              const d = new Date(r.recorded_at);
              return (
                <TableRow key={i} className={`border-gray-200 ${i % 2 ? "bg-white/[0.015]" : ""}`}>
                  <TableCell className="text-xs">{d.toLocaleDateString()}</TableCell>
                  <TableCell className="text-xs">{d.toLocaleTimeString()}</TableCell>
                  <TableCell className="font-mono">{r.value}</TableCell>
                  <TableCell className="text-xs text-gray-500">{r.unit}</TableCell>
                  <TableCell><Badge className={`border-0 ${r.status === "critical" ? "bg-red-500/15 text-red-300" : r.status === "warning" ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{r.status}</Badge></TableCell>
                </TableRow>
              );
            })}
            {!readings.length && <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">No readings.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
